<template>
  <div class="sd-overlay" ref="overlay" tabindex="-1" @keydown="onKeydown">

    <div v-if="loading" class="sd-state-center">
      <rotate-square size="60px" />
      <p class="mt-3 sd-state-text">Carregando diagnóstico...</p>
    </div>

    <div v-else-if="erro" class="sd-state-center">
      <i class="fa fa-exclamation-circle fa-3x mb-3" style="color:#FF8F00"></i>
      <p class="sd-state-text mb-3">{{ erro }}</p>
      <button class="btn btn-light" @click="fechar">
        <i class="fa fa-arrow-left mr-1"></i> Voltar
      </button>
    </div>

    <template v-else-if="relatorio">
      <!-- Header -->
      <div class="sd-header">
        <div class="sd-header-brand">
          <span class="sd-header-title">{{ relatorio.title }}</span>
        </div>
        <div class="sd-header-right">
          <span class="sd-counter">{{ currentSlide + 1 }}&nbsp;/&nbsp;{{ slides.length }}</span>
          <button class="sd-close-btn" @click="fechar" title="Fechar (Esc)">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Progress bar -->
      <div class="sd-progress-track">
        <div class="sd-progress-fill" :style="{ width: progress + '%' }"></div>
      </div>

      <!-- Slide area -->
      <div class="sd-content-area">
        <transition name="sd-fade" mode="out-in">
          <div :key="currentSlide" class="sd-slide-wrapper">

            <!-- ── COVER ── -->
            <div v-if="slide.type === 'cover'" class="sd-cover">
              <div class="sd-cover-icon"><i class="fa fa-leaf"></i></div>
              <h1 class="sd-cover-h1">{{ relatorio.title }}</h1>
              <div class="sd-cover-total">
                <span class="sd-big-num">{{ relatorio.total_responses }}</span>
                <span class="sd-big-label">respostas coletadas</span>
              </div>
              <p v-if="formData && formData.period_label" class="sd-cover-period">
                Edição {{ formData.edition_number }} — {{ formData.period_label }}
              </p>
              <p v-else-if="formData" class="sd-cover-period">Edição {{ formData.edition_number }}</p>
              <p class="sd-cover-hint">
                <i class="fa fa-arrow-right mr-1"></i>
                Use as setas do teclado ou os botões abaixo para navegar
              </p>
            </div>

            <!-- ── PROFILE ── -->
            <div v-else-if="slide.type === 'profile'" class="sd-card">
              <div class="sd-card-header">
                <div class="sd-card-icon"><i class="fas fa-users"></i></div>
                <div>
                  <h2 class="sd-card-title">Perfil dos Respondentes</h2>
                  <p class="sd-card-sub">{{ relatorio.total_responses }} respondentes no total</p>
                </div>
              </div>
              <div class="row mt-3">
                <div v-for="q in slide.questions" :key="q.question_id" class="col-md-4">
                  <p class="sd-mini-label">{{ q.question_text }}</p>
                  <highcharts v-if="q.chartStyle === 'donut'" :options="buildProfileDonut(q)" />
                  <highcharts v-else :options="buildProfileBar(q)" />
                </div>
              </div>
            </div>

            <!-- ── DONUT ── -->
            <div v-else-if="slide.type === 'donut'" class="sd-card">
              <div class="sd-card-header">
                <div class="sd-card-icon"><i class="fas fa-chart-pie"></i></div>
                <div>
                  <h2 class="sd-card-title">{{ slide.question.question_text }}</h2>
                  <p class="sd-card-sub">{{ slide.question.total_answers }} respostas</p>
                </div>
              </div>
              <div class="sd-chart-area">
                <highcharts :options="buildDonutChart(slide.question)" />
              </div>
            </div>

            <!-- ── BAR_H ── -->
            <div v-else-if="slide.type === 'bar_h'" class="sd-card">
              <div class="sd-card-header">
                <div class="sd-card-icon"><i class="fas fa-chart-bar"></i></div>
                <div>
                  <h2 class="sd-card-title">{{ slide.question.question_text }}</h2>
                  <p class="sd-card-sub">{{ slide.question.total_answers }} respostas</p>
                </div>
              </div>
              <div class="sd-chart-area">
                <highcharts :options="buildBarChart(slide.question)" />
              </div>
            </div>

            <!-- ── GAUGE (scale) ── -->
            <div v-else-if="slide.type === 'gauge'" class="sd-card">
              <div class="sd-card-header">
                <div class="sd-card-icon"><i class="fas fa-star-half-alt"></i></div>
                <div>
                  <h2 class="sd-card-title">{{ slide.question.question_text }}</h2>
                  <p class="sd-card-sub">{{ slide.question.total_answers }} respostas</p>
                </div>
              </div>
              <div class="sd-gauge-layout">
                <div class="sd-gauge-score">
                  <span class="sd-gauge-num">{{ slide.question.average_scale }}</span>
                  <span class="sd-gauge-denom">/ {{ slide.question.scale_max || 5 }}</span>
                  <div v-if="slide.question.scale_min_label" class="sd-gauge-labels mt-2">
                    <span class="sd-gauge-min-label">1 = {{ slide.question.scale_min_label }}</span>
                    <span class="sd-gauge-max-label">{{ slide.question.scale_max || 5 }} = {{ slide.question.scale_max_label }}</span>
                  </div>
                </div>
                <div class="sd-gauge-chart">
                  <highcharts :options="buildGaugeChart(slide.question)" />
                </div>
              </div>
            </div>

            <!-- ── LIKERT ── -->
            <div v-else-if="slide.type === 'likert'" class="sd-card">
              <div class="sd-card-header">
                <div class="sd-card-icon"><i class="fas fa-sliders-h"></i></div>
                <div>
                  <h2 class="sd-card-title">{{ slide.question.question_text }}</h2>
                  <p class="sd-card-sub">{{ slide.question.total_answers }} respostas</p>
                </div>
              </div>
              <div class="sd-likert-area mt-3">
                <highcharts :options="buildLikertChart(slide.question)" />
                <div class="sd-likert-highlights mt-4">
                  <div v-for="d in [...slide.question.distribution].reverse()" :key="d.option_id" class="sd-likert-item">
                    <span class="sd-likert-count">{{ d.count }}</span>
                    <span class="sd-likert-label">{{ d.option_text }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── RANKING ── -->
            <div v-else-if="slide.type === 'ranking'" class="sd-card">
              <div class="sd-card-header">
                <div class="sd-card-icon">
                  <i :class="slide.metric === 'count' ? 'fas fa-chart-bar' : 'fas fa-trophy'"></i>
                </div>
                <div>
                  <h2 class="sd-card-title">{{ slide.question.question_text }}</h2>
                  <p class="sd-card-sub">{{ slide.question.total_answers }} respostas</p>
                </div>
              </div>
              <div class="sd-ranking-list mt-3">
                <div
                  v-for="(d, idx) in sortedDist(slide.question)"
                  :key="d.option_id"
                  class="sd-ranking-item"
                >
                  <div class="sd-rank-badge" :class="'rank-' + (idx < 3 ? idx + 1 : 'rest')">
                    {{ idx + 1 }}
                  </div>
                  <div class="sd-rank-text">{{ d.option_text }}</div>
                  <div class="sd-rank-bar-wrap">
                    <div
                      class="sd-rank-bar"
                      :style="{ width: (slide.metric === 'count' ? (d.count / maxCount(slide.question) * 100) : d.percentage) + '%' }"
                    ></div>
                  </div>
                  <div class="sd-rank-pct">{{ slide.metric === 'count' ? d.count : d.percentage + '%' }}</div>
                </div>
              </div>
            </div>

            <!-- ── FREE TAGS ── -->
            <div v-else-if="slide.type === 'free_tags'" class="sd-card">
              <div class="sd-card-header">
                <div class="sd-card-icon"><i class="fas fa-comment-dots"></i></div>
                <div>
                  <h2 class="sd-card-title">{{ slide.question.question_text }}</h2>
                  <p class="sd-card-sub">
                    {{ filteredTextResponses(slide.question).length }} respostas recebidas
                  </p>
                </div>
              </div>
              <div class="sd-tags-area mt-3">
                <template v-if="filteredTextResponses(slide.question).length">
                  <span
                    v-for="(t, i) in filteredTextResponses(slide.question)"
                    :key="i"
                    class="sd-tag"
                  >{{ t }}</span>
                </template>
                <p v-else class="text-muted small">Nenhuma resposta de texto recebida.</p>
              </div>
            </div>

            <!-- ── CONTACT COUNT ── -->
            <div v-else-if="slide.type === 'contact_count'" class="sd-card sd-card-center">
              <div class="sd-contact-icon"><i class="fas fa-handshake"></i></div>
              <p class="sd-contact-label">Engajamento na elaboração do PMAU</p>
              <div class="sd-contact-stats">
                <div v-if="slide.telefoneCount > 0" class="sd-contact-item">
                  <span class="sd-big-num" style="color:#2E7D32">{{ slide.telefoneCount }}</span>
                  <span class="sd-big-label">querem participar das Audiências Públicas</span>
                </div>
                <div v-if="slide.emailCount > 0" class="sd-contact-item">
                  <span class="sd-big-num" style="color:#1565C0">{{ slide.emailCount }}</span>
                  <span class="sd-big-label">querem receber o PMAU por e-mail</span>
                </div>
              </div>
            </div>

            <!-- ── CLOSING ── -->
            <div v-else-if="slide.type === 'closing'" class="sd-cover">
              <div class="sd-cover-icon" style="color:#4CAF50"><i class="fa fa-check-circle"></i></div>
              <h1 class="sd-cover-h1">Obrigado pela participação!</h1>
              <div class="sd-cover-total">
                <span class="sd-big-num">{{ relatorio.total_responses }}</span>
                <span class="sd-big-label">vozes ouvidas</span>
              </div>
              <p class="sd-cover-sub2">
                Estes dados subsidiarão o Plano Municipal de Arborização Urbana (PMAU).
              </p>
              <button class="btn btn-light mt-4" @click="fechar">
                <i class="fa fa-arrow-left mr-1"></i> Voltar ao painel
              </button>
            </div>

          </div>
        </transition>
      </div>

      <!-- Footer -->
      <div class="sd-footer">
        <button class="sd-nav-btn" :disabled="currentSlide === 0" @click="prevSlide" title="Anterior (←)">
          <i class="fa fa-chevron-left"></i>
        </button>
        <div v-if="slides.length <= 22" class="sd-dots">
          <span
            v-for="(s, i) in slides"
            :key="i"
            class="sd-dot"
            :class="{ active: i === currentSlide, 'is-special': s.type === 'cover' || s.type === 'closing' }"
            @click="goToSlide(i)"
            :title="slideTitle(s)"
          ></span>
        </div>
        <span v-else class="sd-counter" style="font-size:14px">
          {{ currentSlide + 1 }} de {{ slides.length }}
        </span>
        <button class="sd-nav-btn" :disabled="currentSlide === slides.length - 1" @click="nextSlide" title="Próximo (→)">
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import SurveyService from "../../servico/SurveyService";
import {
  buildDonutChart as buildDonutChartOptions,
  buildBarChart as buildBarChartOptions,
  buildLikertChart as buildLikertChartOptions,
  buildGaugeChart as buildGaugeChartOptions,
  buildProfileDonut as buildProfileDonutOptions,
  buildProfileBar as buildProfileBarOptions,
  PROFILE_QUESTION_CONFIGS,
} from "../../util/SurveyCharts";

export default {
  name: "SurveyDashboard",
  components: { RotateSquare },

  data() {
    return {
      loading: true,
      erro: null,
      formData: null,
      relatorio: null,
      codeMap: {},
      currentSlide: 0,
      windowHeight: window.innerHeight,
    };
  },

  computed: {
    contaId() {
      return this.$store.getters.getAutenticacao?.contaSelecionada?.id;
    },

    // Altura disponível para o gráfico dentro do card
    // header(54) + progress(3) + footer(62) + content-top(24) + card-padding(64) + card-header(~100) + bottom-margin(24)
    chartH() {
      return Math.max(320, this.windowHeight - 331);
    },

    slide() {
      return this.slides[this.currentSlide] || {};
    },

    progress() {
      if (this.slides.length <= 1) return 100;
      return (this.currentSlide / (this.slides.length - 1)) * 100;
    },

    slides() {
      if (!this.relatorio) return [];

      const qs = this.relatorio.questions || [];
      const questionsWithCode = qs.map(q => ({
        ...q,
        code: this.codeMap[q.question_id] || null,
      }));

      const byCode = {};
      questionsWithCode.forEach(q => { if (q.code) byCode[q.code] = q; });

      const result = [];

      result.push({ type: "cover" });

      // Profile (gender donut + age bar + education bar)
      const profileQs = PROFILE_QUESTION_CONFIGS
        .map(c => byCode[c.code] ? { ...byCode[c.code], chartStyle: c.chartStyle } : null)
        .filter(q => q && q.total_answers > 0);
      if (profileQs.length > 0) {
        result.push({ type: "profile", questions: profileQs });
      }

      // Bairro
      if (byCode["id_bairro"] && byCode["id_bairro"].total_answers > 0) {
        result.push({ type: "bar_h", question: byCode["id_bairro"] });
      }

      // Evaluation slides in order
      const evalOrder = [
        ["eval_classification", "donut"],
        ["eval_beauty_scale", "gauge"],
        ["eval_mental_health", "bar_h"],
        ["eval_problems", "ranking", "count"],
        ["eval_maintenance", "bar_h"],
        ["eval_investment_likert", "likert"],
        ["eval_urban_trees", "likert"],
        ["eval_wishes", "ranking", "count"],
        ["eval_priority", "ranking", "percentage"],
        ["eval_more_trees", "donut"],
        ["eval_species", "free_tags"],
      ];
      evalOrder.forEach(([code, type, metric]) => {
        if (byCode[code] && byCode[code].total_answers > 0) {
          result.push({ type, question: byCode[code], metric });
        }
      });

      // Custom questions (no code)
      questionsWithCode
        .filter(q => !q.code && !q.is_archived && q.total_answers > 0)
        .forEach(q => {
          const type = q.question_type === "scale" ? "gauge"
                     : q.question_type === "free_text" ? "free_tags"
                     : q.question_type === "multiple_choice" ? "ranking"
                     : "bar_h";
          const metric = q.question_type === "multiple_choice" ? "count" : "percentage";
          result.push({ type, question: q, metric });
        });

      // Contact engagement slide
      const telefoneQ = byCode["id_telefone"];
      const emailQ = byCode["id_email"];
      const telefoneCount = this.filteredTextResponses(telefoneQ).length;
      const emailCount = this.filteredTextResponses(emailQ).length;
      if (telefoneCount + emailCount > 0) {
        result.push({ type: "contact_count", telefoneCount, emailCount });
      }

      result.push({ type: "closing" });

      return result;
    },
  },

  beforeMount() {
    this.loadDashboard();
  },

  mounted() {
    this._onResize = () => { this.windowHeight = window.innerHeight; };
    window.addEventListener("resize", this._onResize);
    this.$nextTick(() => {
      if (this.$refs.overlay) this.$refs.overlay.focus();
    });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
  },

  methods: {
    async loadDashboard() {
      try {
        const contaId = this.contaId;
        if (!contaId) {
          this.erro = "Não foi possível identificar a conta.";
          return;
        }

        const formResp = await SurveyService.GetForm(contaId);
        this.formData = formResp.data;

        const [baseResp, reportResp] = await Promise.all([
          SurveyService.ListBaseQuestions(),
          SurveyService.GetReport(this.formData.id),
        ]);

        this.codeMap = {};
        baseResp.data.forEach(q => { this.codeMap[q.id] = q.code; });
        this.relatorio = reportResp.data;
      } catch (e) {
        this.erro = "Erro ao carregar os dados do diagnóstico.";
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          if (this.$refs.overlay) this.$refs.overlay.focus();
        });
      }
    },

    prevSlide() {
      if (this.currentSlide > 0) this.currentSlide--;
    },

    nextSlide() {
      if (this.currentSlide < this.slides.length - 1) this.currentSlide++;
    },

    goToSlide(i) {
      this.currentSlide = i;
    },

    onKeydown(e) {
      if (["ArrowRight", "ArrowDown", " "].includes(e.key)) {
        e.preventDefault();
        this.nextSlide();
      } else if (["ArrowLeft", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        this.prevSlide();
      } else if (e.key === "Escape") {
        this.fechar();
      }
    },

    fechar() {
      this.$router.push({ name: "PmauSurvey" });
    },

    slideTitle(s) {
      if (s.type === "cover") return "Capa";
      if (s.type === "profile") return "Perfil dos respondentes";
      if (s.type === "closing") return "Encerramento";
      if (s.type === "contact_count") return "Engajamento";
      return s.question ? s.question.question_text.slice(0, 50) : "";
    },

    sortedDist(question) {
      return [...(question.distribution || [])].sort((a, b) => b.count - a.count);
    },

    maxCount(question) {
      const dist = question.distribution || [];
      return Math.max(1, ...dist.map(d => d.count));
    },

    filteredTextResponses(question) {
      if (!question) return [];
      return (question.text_responses || []).filter(r => r && r.trim());
    },

    // ── Chart builders (delegam para o módulo compartilhado SurveyCharts.js) ──

    buildDonutChart(question) {
      return buildDonutChartOptions(question, this.chartH);
    },

    buildBarChart(question) {
      return buildBarChartOptions(question, this.chartH);
    },

    buildLikertChart(question) {
      return buildLikertChartOptions(question, this.chartH);
    },

    buildGaugeChart(question) {
      return buildGaugeChartOptions(question, this.chartH);
    },

    buildProfileDonut(question) {
      return buildProfileDonutOptions(question);
    },

    buildProfileBar(question) {
      return buildProfileBarOptions(question);
    },
  },
};
</script>

<style scoped>
/* ── Overlay full-screen ────────────────────────────────────────── */
.sd-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #0d2818;
  display: flex;
  flex-direction: column;
  outline: none;
  font-family: inherit;
}

/* ── Loading / Error ────────────────────────────────────────────── */
.sd-state-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.sd-state-text {
  color: rgba(255,255,255,0.8);
  font-size: 15px;
}

/* ── Header ─────────────────────────────────────────────────────── */
.sd-header {
  flex-shrink: 0;
  height: 54px;
  background: #1B4332;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}
.sd-header-brand {
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  min-width: 0;
}
.sd-header-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}
.sd-counter {
  color: rgba(255,255,255,0.65);
  font-size: 13px;
  white-space: nowrap;
}
.sd-close-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.65);
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
  line-height: 1;
}
.sd-close-btn:hover { color: #fff; background: rgba(255,255,255,0.15); }

/* ── Progress bar ───────────────────────────────────────────────── */
.sd-progress-track {
  flex-shrink: 0;
  height: 3px;
  background: rgba(255,255,255,0.12);
}
.sd-progress-fill {
  height: 100%;
  background: #4CAF50;
  transition: width 0.3s ease;
}

/* ── Content area ───────────────────────────────────────────────── */
.sd-content-area {
  flex: 1;
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 32px 0;
}
.sd-slide-wrapper {
  width: 100%;
  max-width: 1280px;
  display: flex;
  flex-direction: column;
  padding-bottom: 24px;
}

/* ── Transitions ────────────────────────────────────────────────── */
.sd-fade-enter-active { transition: all 0.22s ease; }
.sd-fade-leave-active { transition: all 0.18s ease; }
.sd-fade-enter, .sd-fade-leave-to { opacity: 0; transform: translateY(8px); }

/* ── Cover ──────────────────────────────────────────────────────── */
.sd-cover {
  text-align: center;
  padding: 48px 16px 64px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.sd-cover-icon {
  font-size: 72px;
  color: #4CAF50;
  margin-bottom: 24px;
}
.sd-cover-h1 {
  font-size: 42px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 36px;
  line-height: 1.25;
  max-width: 860px;
}
.sd-cover-total {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  background: rgba(76,175,80,0.15);
  border: 1px solid rgba(76,175,80,0.35);
  border-radius: 16px;
  padding: 28px 64px;
  margin-bottom: 24px;
}
.sd-big-num {
  font-size: 90px;
  font-weight: 800;
  color: #4CAF50;
  line-height: 1;
  display: block;
}
.sd-big-label {
  font-size: 18px;
  margin-top: 6px;
  display: block;
}
.sd-cover-period {
  color: rgba(255,255,255,0.55);
  font-size: 18px;
  margin: 0 0 6px;
}
.sd-cover-sub2 {
  color: rgba(255,255,255,0.5);
  font-size: 18px;
  max-width: 560px;
  margin: 8px 0 0;
  line-height: 1.5;
}
.sd-cover-hint {
  color: rgba(255,255,255,0.35);
  font-size: 15px;
  margin-top: 32px;
}

/* ── Cards ──────────────────────────────────────────────────────── */
.sd-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 40px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.35);
  flex: 1;
}
.sd-card-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.sd-card-header {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 8px;
}
.sd-card-icon {
  flex-shrink: 0;
  width: 54px;
  height: 54px;
  background: #E8F5E9;
  color: #2E7D32;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}
.sd-card-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2a;
  margin: 0 0 4px;
  line-height: 1.35;
}
.sd-card-sub {
  font-size: 15px;
  color: #777;
  margin: 0;
}
.sd-chart-area { margin-top: 8px; }

/* ── Gauge ──────────────────────────────────────────────────────── */
.sd-gauge-layout {
  display: flex;
  align-items: center;
  gap: 40px;
  margin-top: 16px;
}
.sd-gauge-score {
  flex-shrink: 0;
  text-align: center;
  min-width: 170px;
}
.sd-gauge-num {
  display: block;
  font-size: 96px;
  font-weight: 800;
  color: #2E7D32;
  line-height: 1;
}
.sd-gauge-denom {
  font-size: 28px;
  color: #999;
}
.sd-gauge-labels {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
}
.sd-gauge-min-label { color: #E53935; }
.sd-gauge-max-label { color: #2E7D32; }
.sd-gauge-chart { flex: 1; min-width: 0; }

/* ── Likert ─────────────────────────────────────────────────────── */
.sd-likert-highlights {
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-wrap: wrap;
}
.sd-likert-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.sd-likert-count {
  font-size: 42px;
  font-weight: 800;
  color: #2E7D32;
}
.sd-likert-label {
  font-size: 14px;
  color: #666;
  text-align: center;
  max-width: 160px;
}

/* ── Ranking ────────────────────────────────────────────────────── */
.sd-ranking-list { display: flex; flex-direction: column; gap: 14px; }
.sd-ranking-item { display: flex; align-items: center; gap: 14px; }
.sd-rank-badge {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  background: #9E9E9E;
}
.sd-rank-badge.rank-1 { background: #F9A825; }
.sd-rank-badge.rank-2 { background: #78909C; }
.sd-rank-badge.rank-3 { background: #8D6E63; }
.sd-rank-text { min-width: 0; flex: 1; font-size: 17px; color: #333; line-height: 1.35; }
.sd-rank-bar-wrap {
  flex: 0 0 220px;
  height: 10px;
  background: #E8F5E9;
  border-radius: 5px;
  overflow: hidden;
}
.sd-rank-bar {
  height: 100%;
  background: #2E7D32;
  border-radius: 5px;
  transition: width 0.5s ease;
}
.sd-rank-pct {
  flex-shrink: 0;
  width: 52px;
  text-align: right;
  font-size: 17px;
  font-weight: 700;
  color: #2E7D32;
}

/* ── Profile mini charts ────────────────────────────────────────── */
.sd-mini-label {
  font-size: 16px;
  font-weight: 600;
  color: #555;
  text-align: center;
  margin-bottom: 4px;
}

/* ── Free tags ──────────────────────────────────────────────────── */
.sd-tags-area {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-height: 380px;
  overflow-y: auto;
}
.sd-tag {
  display: inline-block;
  background: #E8F5E9;
  color: #2E7D32;
  border: 1px solid #A5D6A7;
  border-radius: 24px;
  padding: 8px 18px;
  font-size: 16px;
}

/* ── Contact count ──────────────────────────────────────────────── */
.sd-contact-icon { font-size: 64px; color: #4CAF50; margin-bottom: 16px; }
.sd-contact-label { font-size: 22px; font-weight: 600; color: #333; margin-bottom: 28px; }
.sd-contact-stats { display: flex; gap: 56px; flex-wrap: wrap; justify-content: center; }
.sd-contact-item { display: flex; flex-direction: column; align-items: center; gap: 6px; }

/* ── Footer ─────────────────────────────────────────────────────── */
.sd-footer {
  flex-shrink: 0;
  height: 62px;
  background: #1B4332;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}
.sd-nav-btn {
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  color: #fff;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sd-nav-btn:hover:not(:disabled) { background: rgba(255,255,255,0.22); }
.sd-nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.sd-dots {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 560px;
  overflow: hidden;
  max-height: 30px;
}
.sd-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  flex-shrink: 0;
}
.sd-dot.active { background: #4CAF50; transform: scale(1.5); }
.sd-dot.is-special { background: rgba(255,255,255,0.45); }
.sd-dot:hover:not(.active) { background: rgba(255,255,255,0.55); }
</style>
